/**
 * C-GTCC-VIEW-DIAG-AIA1 — the "Rohpaket-Decoder" of the reference
 * (`GTCC_REFERENCE_2026-09-01.html:665`): "Codec8-Paket (hex) einfügen →
 * lesbar zerlegt … Zeigt Zeit, Koordinate, Zündung, IO-Elemente".
 *
 * The instruction asks for a pure client-side, read-only decomposition. There
 * is no decode endpoint in `services/api/src` and `apps/admin` cannot import
 * `services/api`, so this module mirrors the authoritative parser
 * `services/api/src/gateway/codec8.ts` (and its CRC in `crc16.ts`) on the
 * browser's `DataView` instead of Node's `Buffer`. Every constant, every
 * field order and every rejection reason below is that parser's — the module
 * invents no framing of its own.
 *
 * Two deliberate differences, both because this is an operator tool and not
 * an ingest path:
 *   - it decodes exactly one pasted frame instead of a streaming buffer, so
 *     `MAX_PARTIAL_BYTES` has no counterpart here;
 *   - it returns a verdict instead of throwing, so the panel can print *why*
 *     a packet did not decode rather than a blank.
 *
 * Ignition follows the Owner decision recorded at
 * `services/api/src/gateway/bridge.ts`: DIN1 (IO 1) has priority; generic
 * ignition (IO 239) is the fallback (Owner decision 2026-10-25). Both absent
 * or non-binary stay null; ignition is never inferred from speed.
 *
 * Read-only by construction: no import of any API client, no network call.
 */

import {
  decodeIgnitionIo,
  IO_IGNITION_DIN1 as CODEC8_IO_IGNITION_DIN1,
  IO_IGNITION_GENERIC as CODEC8_IO_IGNITION_GENERIC,
} from "@genius-tracker/api-client/ignition-io";

export { decodeIgnitionIo, CODEC8_IO_IGNITION_DIN1, CODEC8_IO_IGNITION_GENERIC };

/** Mirrors `codec8.ts:41`. */
export const CODEC8_MAX_FRAME_BYTES = 64 * 1024;
/** Mirrors `codec8.ts:44-45`. */
export const CODEC8_ID = 0x08;
export const CODEC8_EXT_ID = 0x8e;
export type Codec8DecodeCodec = "codec8" | "codec8ext";

/** The rejection reasons of `ParseError` plus the two this tool adds. */
export type Codec8DecodeReason =
  | "empty"
  | "bad_hex"
  | "bad_preamble"
  | "bad_length"
  | "bad_codec"
  | "record_count_mismatch"
  | "truncated"
  | "bad_crc"
  | "bad_field";

export interface Codec8DecodeIoValue {
  readonly id: number;
  /** Decimal text: IO values are up to 64 bit, so they never become numbers. */
  readonly value: string;
}

export interface Codec8DecodeRecord {
  /** Device time as ISO-8601 — the packet's own clock, not the browser's. */
  readonly timestampIso: string;
  readonly priority: number;
  readonly latitudeDeg: number;
  readonly longitudeDeg: number;
  readonly altitudeM: number;
  readonly angleDeg: number;
  readonly satellites: number;
  readonly speedKmh: number;
  readonly eventIoId: number;
  /** `null` = not decidable from this packet; never guessed. */
  readonly ignitionOn: boolean | null;
  readonly io: readonly Codec8DecodeIoValue[];
}

export type Codec8DecodeResult =
  | {
      readonly ok: true;
      readonly codec: Codec8DecodeCodec;
      readonly frameBytes: number;
      readonly records: readonly Codec8DecodeRecord[];
    }
  | { readonly ok: false; readonly reason: Codec8DecodeReason; readonly detail: string };

/* ------------------------------------------------------------------- hex */

/**
 * Accepts the shapes an operator actually pastes: `00 00 00 00`, `0x…`,
 * newlines, tabs. Rejects everything else rather than silently dropping it.
 */
export function parseCodec8Hex(input: string): Uint8Array | null {
  const cleaned = input.replace(/0[xX]/g, "").replace(/[\s:_-]+/g, "");
  if (cleaned.length === 0 || cleaned.length % 2 !== 0) return null;
  if (!/^[0-9a-fA-F]+$/.test(cleaned)) return null;
  const out = new Uint8Array(cleaned.length / 2);
  for (let i = 0; i < out.length; i += 1) {
    out[i] = Number.parseInt(cleaned.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

/** Mirrors `crc16.ts:11` — CRC-16/IBM, poly 0xA001, init 0x0000. */
export function codec8Crc16Ibm(bytes: Uint8Array): number {
  let crc = 0x0000;
  for (let i = 0; i < bytes.length; i += 1) {
    crc ^= bytes[i]! & 0xff;
    for (let j = 0; j < 8; j += 1) {
      crc = (crc & 0x0001) !== 0 ? (crc >>> 1) ^ 0xa001 : crc >>> 1;
    }
  }
  return crc & 0xffff;
}

/* ---------------------------------------------------------------- reader */

class DecodeError extends Error {
  constructor(
    readonly reason: Codec8DecodeReason,
    message: string,
  ) {
    super(message);
    this.name = "DecodeError";
  }
}

/** Mirrors `codec8.ts:322` `ByteReader`, on `DataView`. */
class ByteReader {
  private readonly view: DataView;

  constructor(
    private readonly bytes: Uint8Array,
    public pos: number,
  ) {
    this.view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  }

  private need(n: number): void {
    if (this.pos + n > this.bytes.length) {
      throw new DecodeError(
        "truncated",
        `need ${n} bytes at pos ${this.pos}, have ${this.bytes.length - this.pos}`,
      );
    }
  }

  u8(): number {
    this.need(1);
    const v = this.view.getUint8(this.pos);
    this.pos += 1;
    return v;
  }
  u16(): number {
    this.need(2);
    const v = this.view.getUint16(this.pos);
    this.pos += 2;
    return v;
  }
  i16(): number {
    this.need(2);
    const v = this.view.getInt16(this.pos);
    this.pos += 2;
    return v;
  }
  u32(): number {
    this.need(4);
    const v = this.view.getUint32(this.pos);
    this.pos += 4;
    return v;
  }
  i32(): number {
    this.need(4);
    const v = this.view.getInt32(this.pos);
    this.pos += 4;
    return v;
  }
  u64(): bigint {
    this.need(8);
    const v = this.view.getBigUint64(this.pos);
    this.pos += 8;
    return v;
  }
  skip(n: number): void {
    this.need(n);
    this.pos += n;
  }
}

/* --------------------------------------------------------------- decoder */

/** Decodes exactly one complete Codec 8 / Codec 8-Extended frame. */
export function decodeCodec8Frame(input: string): Codec8DecodeResult {
  if (input.trim().length === 0) return { ok: false, reason: "empty", detail: "" };

  const bytes = parseCodec8Hex(input);
  if (bytes === null) {
    return { ok: false, reason: "bad_hex", detail: "not an even-length hexadecimal string" };
  }

  try {
    return decodeBytes(bytes);
  } catch (error) {
    if (error instanceof DecodeError) {
      return { ok: false, reason: error.reason, detail: error.message };
    }
    /* Never swallow an unexpected fault into a false "ok". */
    return {
      ok: false,
      reason: "bad_field",
      detail: error instanceof Error ? error.message : String(error),
    };
  }
}

function decodeBytes(bytes: Uint8Array): Codec8DecodeResult {
  /* Header — mirrors `peekFramedLength` (`codec8.ts:158`). */
  if (bytes.length < 12) {
    throw new DecodeError("truncated", `frame shorter than the 12-byte envelope (${bytes.length})`);
  }
  const head = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (head.getUint32(0) !== 0x00000000) {
    throw new DecodeError(
      "bad_preamble",
      `expected 0x00000000 preamble, got 0x${head.getUint32(0).toString(16)}`,
    );
  }
  const dataLen = head.getUint32(4);
  if (dataLen <= 0) throw new DecodeError("bad_length", `non-positive data length ${dataLen}`);
  const framedLen = 8 + dataLen + 4;
  if (framedLen > CODEC8_MAX_FRAME_BYTES) {
    throw new DecodeError(
      "bad_length",
      `declared frame length ${framedLen} exceeds ${CODEC8_MAX_FRAME_BYTES}`,
    );
  }
  if (bytes.length < framedLen) {
    throw new DecodeError("truncated", `frame declares ${framedLen} bytes, pasted ${bytes.length}`);
  }

  /* CRC over the data span only — mirrors `parseFrame` (`codec8.ts:173`). */
  const data = bytes.subarray(8, 8 + dataLen);
  const declaredCrc = head.getUint32(8 + dataLen);
  const computed = codec8Crc16Ibm(data);
  if (computed !== (declaredCrc & 0xffff)) {
    throw new DecodeError(
      "bad_crc",
      `CRC mismatch: declared 0x${(declaredCrc & 0xffff).toString(16)}, computed 0x${computed.toString(16)}`,
    );
  }

  if (data.length < 2) throw new DecodeError("truncated", "data span shorter than 2 bytes");
  const codecId = data[0]!;
  if (codecId !== CODEC8_ID && codecId !== CODEC8_EXT_ID) {
    throw new DecodeError("bad_codec", `unknown codec id 0x${codecId.toString(16)}`);
  }
  const codec: Codec8DecodeCodec = codecId === CODEC8_ID ? "codec8" : "codec8ext";

  const numberOfData1 = data[1]!;
  const reader = new ByteReader(data, 2);
  const records: Codec8DecodeRecord[] = [];
  for (let i = 0; i < numberOfData1; i += 1) records.push(readRecord(reader, codec));

  const numberOfData2 = reader.u8();
  if (numberOfData2 !== numberOfData1) {
    throw new DecodeError(
      "record_count_mismatch",
      `Number of Data 1 = ${numberOfData1} vs Number of Data 2 = ${numberOfData2}`,
    );
  }
  if (reader.pos !== data.length) {
    throw new DecodeError(
      "truncated",
      `trailing bytes after last record: pos=${reader.pos}, end=${data.length}`,
    );
  }

  return { ok: true, codec, frameBytes: framedLen, records };
}

function readRecord(r: ByteReader, codec: Codec8DecodeCodec): Codec8DecodeRecord {
  const ts = r.u64();
  const priority = r.u8();

  const lonRaw = r.i32();
  const latRaw = r.i32();
  const altitudeM = r.i16();
  const angleDeg = r.u16();
  const satellites = r.u8();
  const speedKmh = r.u16();
  /* Teltonika encodes lat/lng as int32 "E7" (degrees * 1e7). */
  const longitudeDeg = lonRaw / 1e7;
  const latitudeDeg = latRaw / 1e7;
  if (longitudeDeg < -180 || longitudeDeg > 180) {
    throw new DecodeError("bad_field", `longitude out of range: ${longitudeDeg}`);
  }
  if (latitudeDeg < -90 || latitudeDeg > 90) {
    throw new DecodeError("bad_field", `latitude out of range: ${latitudeDeg}`);
  }

  const isExt = codec === "codec8ext";
  const eventIoId = isExt ? r.u16() : r.u8();
  const nTotal = isExt ? r.u16() : r.u8();
  const values = new Map<number, bigint>();
  let parsedTotal = 0;
  const readId = (): number => (isExt ? r.u16() : r.u8());

  const readGroup = (size: 1 | 2 | 4 | 8): void => {
    const count = isExt ? r.u16() : r.u8();
    for (let i = 0; i < count; i += 1) {
      const id = readId();
      const value =
        size === 1
          ? BigInt(r.u8())
          : size === 2
            ? BigInt(r.u16())
            : size === 4
              ? BigInt(r.u32())
              : r.u64();
      values.set(id, value);
      parsedTotal += 1;
    }
  };
  readGroup(1);
  readGroup(2);
  readGroup(4);
  readGroup(8);
  if (isExt) {
    /* Variable-length group: the ingest parser records presence only, so this
       decoder shows the id with value 0 exactly as the server does. */
    const count = r.u16();
    for (let i = 0; i < count; i += 1) {
      const id = r.u16();
      const len = r.u16();
      r.skip(len);
      if (!values.has(id)) values.set(id, 0n);
      parsedTotal += 1;
    }
  }
  if (parsedTotal !== nTotal) {
    throw new DecodeError(
      "bad_field",
      `IO element count mismatch: N=${nTotal}, parsed=${parsedTotal}`,
    );
  }

  const ignitionOn = decodeIgnitionIo(values);

  return {
    timestampIso: new Date(Number(ts)).toISOString(),
    priority,
    latitudeDeg,
    longitudeDeg,
    altitudeM,
    angleDeg,
    satellites,
    speedKmh,
    eventIoId,
    ignitionOn,
    io: [...values.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([id, value]) => ({ id, value: value.toString() })),
  };
}
